import { type UseQueryOptions, useQuery } from '@lumapps/base-api/react-query';

import { getSpaceData, spaceQueryKeys } from '../api';
import type { SpaceData, SpaceDataApiOptions } from '../types';

/**
 * Options for useSpaceDataQuery hook.
 * Combines API options (previewMode) with React Query options (enabled, retry, etc.)
 */
export type UseSpaceDataQueryOptions = SpaceDataApiOptions & Omit<UseQueryOptions<SpaceData>, 'queryKey' | 'queryFn'>;

/**
 * React Query hook to fetch space data by ID.
 *
 * **Note:** This hook only fetches data and does NOT populate Redux.
 * For most use cases, prefer `useSpaceDataFromRedux` which fetches AND syncs to Redux.
 *
 * @param id - The space ID to fetch
 * @param options - Optional API options (previewMode) and React Query options (enabled, retry, etc.)
 * @returns Query result with space data
 */
export const useSpaceDataQuery = (id: string, options?: UseSpaceDataQueryOptions) => {
    // Extract API options from the combined options
    const { previewMode, ...queryOptions } = options || {};
    // Only include API options in the key/request when set, so that calls without
    // a previewMode share the same cache entry as `spaceQueryKeys.data.byId(id)`.
    const apiOptions: SpaceDataApiOptions | undefined = previewMode ? { previewMode } : undefined;

    return useQuery<SpaceData>({
        ...queryOptions,
        queryKey: spaceQueryKeys.data.byId(id, apiOptions),
        queryFn: async () => {
            const { data } = await getSpaceData(id, apiOptions ?? {});
            return data;
        },
        enabled: queryOptions?.enabled !== undefined ? queryOptions.enabled : !!id,
    });
};
